import type { TFunction } from 'i18next';

export interface SudokuGameStats {
  errors: number;
  pencilErrors: number;
  hintsUsed: number;
  coverageUsed: boolean;
  pencilMarksEntered: boolean;
  autoPencilUsed: boolean;
}

interface ChallengeContext {
  stats: SudokuGameStats;
  elapsedTime: number;
}

export interface SudokuChallenge {
  key: string;
  progression?: 'orLevel' | 'canLevel';
}

interface ChallengeRule extends SudokuChallenge {
  matches: (context: ChallengeContext) => boolean;
}

const challengeRules: readonly ChallengeRule[] = [
  { matches: ({ stats }) => stats.errors > 2, key: 'challengeFewerErrors' },
  { matches: ({ stats }) => stats.errors > 0, key: 'challengeNoErrors' },
  { matches: ({ stats }) => stats.pencilErrors > 0, key: 'challengeNoPencilErrors' },
  { matches: ({ stats }) => stats.hintsUsed > 2, key: 'challengeFewerHints' },
  { matches: ({ stats }) => stats.hintsUsed > 0, key: 'challengeNoHints' },
  { matches: ({ stats }) => stats.autoPencilUsed, key: 'challengeNoAutoPencil' },
  { matches: ({ stats }) => stats.coverageUsed, key: 'challengeNoCoverage' },
  { matches: ({ stats }) => stats.pencilMarksEntered, key: 'challengeNoPencil' },
  {
    matches: ({ elapsedTime }) => elapsedTime > 600,
    key: 'challengeFasterTime',
    progression: 'orLevel'
  },
  { matches: () => true, key: 'challengeImpressiveTime', progression: 'canLevel' }
];

const nextDifficultyKey: Readonly<Record<number, string | undefined>> = {
  1: 'medium',
  2: 'hard',
  3: 'expert',
  4: undefined
};

export const selectSudokuChallenge = (
  stats: SudokuGameStats,
  elapsedTime: number
): SudokuChallenge => {
  return challengeRules.find(rule => rule.matches({ stats, elapsedTime }))!;
};

export const getChallengeAdditionalText = (
  challenge: SudokuChallenge,
  difficulty: number,
  t: TFunction
): string => {
  const levelKey = nextDifficultyKey[difficulty];
  if (!challenge.progression || !levelKey) {
    return '';
  }

  return ` ${t(challenge.progression, { level: t(levelKey) })}`;
};
